import React, { useState, useEffect } from "react";
import { relatorios, exportData, downloadExcel } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Download,
  RefreshCw,
  Loader2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  Calendar,
} from "lucide-react";
import { formatCurrency } from "../../lib/utils";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { cn } from "../../lib/utils";

const getDefaultStartDate = () => "2025-05-01";
const getDefaultEndDate = () => new Date().toISOString().split("T")[0];

export function RelatoriosPage() {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dreData, setDreData] = useState(null);
  const [dataInicio, setDataInicio] = useState(getDefaultStartDate());
  const [dataFim, setDataFim] = useState(getDefaultEndDate());

  const fetchDRE = async () => {
    setLoading(true);
    try {
      const response = await relatorios.dre(dataInicio, dataFim);
      setDreData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar DRE");
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDRE();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDRE();
  };

  const handleExportDRE = async () => {
    try {
      await downloadExcel(
        () => exportData.dre(dataInicio, dataFim),
        `dre_${dataInicio}_${dataFim}.xlsx`
      );
      toast.success("DRE exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar DRE");
    }
  };

  const DREReport = () => {
    if (!dreData) return null;

    const chartData = [
      { name: "Receita Bruta", valor: dreData.receita_bruta, fill: "#059669" },
      { name: "Impostos", valor: dreData.deducoes[0]?.valor || 0, fill: "#dc2626" },
      { name: "Receita Líquida", valor: dreData.receita_liquida, fill: "#0ea5e9" },
      { name: "CPV", valor: dreData.custos[0]?.valor || 0, fill: "#f59e0b" },
      { name: "Lucro Bruto", valor: dreData.lucro_bruto, fill: "#22c55e" },
      { name: "Despesas", valor: dreData.despesas_operacionais.reduce((acc, d) => acc + d.valor, 0), fill: "#ef4444" },
      { name: "Resultado", valor: dreData.resultado_operacional, fill: dreData.resultado_operacional >= 0 ? "#10b981" : "#ef4444" },
    ];

    return (
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap bg-slate-50 p-4 rounded-lg">
          <Calendar className="h-5 w-5 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">De:</Label>
            <Input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="w-[160px]"
              data-testid="dre-data-inicio"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Até:</Label>
            <Input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="w-[160px]"
              data-testid="dre-data-fim"
            />
          </div>
          <Button onClick={fetchDRE} variant="default" data-testid="dre-filter-btn">
            Aplicar Período
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Receita Líquida</p>
                  <p className="text-2xl font-bold">{formatCurrency(dreData.receita_liquida)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Lucro Bruto</p>
                  <p className="text-2xl font-bold">{formatCurrency(dreData.lucro_bruto)}</p>
                  <p className="text-xs text-muted-foreground">Margem: {dreData.margem_bruta}%</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className={cn(
            "border-l-4",
            dreData.resultado_operacional >= 0 ? "border-l-emerald-500" : "border-l-red-500"
          )}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Resultado Operacional</p>
                  <p className={cn(
                    "text-2xl font-bold",
                    dreData.resultado_operacional >= 0 ? "text-emerald-600" : "text-red-600"
                  )}>
                    {formatCurrency(dreData.resultado_operacional)}
                  </p>
                  <p className="text-xs text-muted-foreground">Margem: {dreData.margem_operacional}%</p>
                </div>
                {dreData.resultado_operacional >= 0 ? (
                  <TrendingUp className="h-8 w-8 text-emerald-500" />
                ) : (
                  <TrendingDown className="h-8 w-8 text-red-500" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Despesas</p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(dreData.despesas_operacionais.reduce((acc, d) => acc + d.valor, 0))}
                  </p>
                </div>
                <Wallet className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* DRE Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">DRE Gerencial - {dreData.periodo}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="dre-table">
                {dreData.itens.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      "flex justify-between items-center py-2 px-3 rounded",
                      item.tipo === "resultado" && "bg-slate-100 font-semibold",
                      item.tipo === "receita" && "bg-green-50",
                      item.tipo === "deducao" && "text-red-600",
                      item.tipo === "custo" && "text-amber-600",
                      item.tipo === "despesa" && "text-red-500"
                    )}
                  >
                    <span className="text-sm">{item.descricao}</span>
                    <div className="flex items-center gap-4">
                      <span className={cn(
                        "text-sm font-medium",
                        item.valor < 0 && "text-red-600"
                      )}>
                        {formatCurrency(Math.abs(item.valor))}
                      </span>
                      {item.percentual !== null && item.percentual !== undefined && (
                        <span className="text-xs text-muted-foreground w-16 text-right">
                          {item.percentual}%
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Visão Gráfica</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="valor" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, index) => (
                      <Bar key={index} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Expenses Breakdown */}
        {dreData.despesas_operacionais.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Despesas por Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dreData.despesas_operacionais.map((desp, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <span className="text-sm font-medium">{desp.descricao}</span>
                    <div className="text-right">
                      <p className="font-semibold text-red-600">{formatCurrency(desp.valor)}</p>
                      <p className="text-xs text-muted-foreground">{desp.percentual}% da receita</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="relatorios-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            Relatórios Financeiros
          </h1>
          <p className="text-muted-foreground">DRE Gerencial</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button onClick={handleExportDRE} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* DRE Content */}
      {loading ? (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : (
        <DREReport />
      )}
    </div>
  );
}
